import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Loader2 } from 'lucide-react';
import { VisibilitySelector } from './VisibilitySelector';

const COMPLIANCE_TYPES = [
  { value: 'keuring', label: 'Keuring (Inspectie)' },
  { value: 'contract', label: 'Contract' },
  { value: 'onderhoud', label: 'Onderhoud (Maintenance)' },
  { value: 'voertuig', label: 'Voertuig (Vehicle)' },
  { value: 'vergunning', label: 'Vergunning (Permit)' },
  { value: 'betaling', label: 'Betaling (Payment)' },
];

const RECURRENCE_OPTIONS = [
  { value: 'one-time', label: 'Eenmalig' },
  { value: 'monthly', label: 'Maandelijks' },
  { value: 'quarterly', label: 'Per kwartaal (3 maanden)' },
  { value: 'half-yearly', label: 'Halfjaarlijks (6 maanden)' },
  { value: 'yearly', label: 'Jaarlijks (12 maanden)' },
];

const REMINDER_PRESETS = [
  { value: '60,30,7', label: '60, 30, 7 dagen' },
  { value: '30,14,7', label: '30, 14, 7 dagen' },
  { value: '90,60,30,7', label: '90, 60, 30, 7 dagen' },
  { value: '14,7,1', label: '14, 7, 1 dag' },
  { value: '7', label: '7 dagen' },
];

export default function ComplianceItemForm({ open, onOpenChange, item, locations, users, onSuccess }) {
  const { authAxios, user: currentUser } = useAuth();
  const [saving, setSaving] = useState(false);
  const isEdit = !!item;

  const [form, setForm] = useState({
    location_id: '',
    type: '',
    title: '',
    description: '',
    due_date: '',
    recurrence_interval: 'one-time',
    reminder_days: '60,30,7',
    assigned_to: '',
    link_to_vault_entry: '',
    visibility: 'normal',
    visibility_granted_users: [],
  });

  useEffect(() => {
    if (item) {
      setForm({
        location_id: item.location_id || '',
        type: item.type || '',
        title: item.title || '',
        description: item.description || '',
        due_date: item.due_date ? item.due_date.slice(0, 10) : '',
        recurrence_interval: item.recurrence?.interval || 'one-time',
        reminder_days: (item.reminder_days || [60, 30, 7]).join(','),
        assigned_to: item.assigned_to || '',
        link_to_vault_entry: item.link_to_vault_entry || '',
        visibility: item.visibility || 'normal',
        visibility_granted_users: item.visibility_granted_users || [],
      });
    } else {
      setForm({
        location_id: '',
        type: '',
        title: '',
        description: '',
        due_date: '',
        recurrence_interval: 'one-time',
        reminder_days: '60,30,7',
        assigned_to: '',
        link_to_vault_entry: '',
        visibility: 'normal',
        visibility_granted_users: [],
      });
    }
  }, [item, open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.location_id || !form.type || !form.due_date || !form.assigned_to) {
      toast.error('Vul alle verplichte velden in');
      return;
    }
    setSaving(true);
    try {
      const api = authAxios();
      const reminderDays = form.reminder_days
        .split(',')
        .map(d => parseInt(d.trim(), 10))
        .filter(d => !isNaN(d) && d > 0);

      const payload = {
        location_id: form.location_id,
        type: form.type,
        title: form.title,
        description: form.description,
        due_date: new Date(form.due_date + 'T00:00:00Z').toISOString(),
        recurrence: {
          interval: form.recurrence_interval,
        },
        reminder_days: reminderDays.length > 0 ? reminderDays : [60, 30, 7],
        assigned_to: form.assigned_to,
        link_to_vault_entry: form.link_to_vault_entry || null,
        visibility: form.visibility || 'normal',
        visibility_granted_users: form.visibility_granted_users || [],
      };

      if (isEdit) {
        await api.put(`/compliance-items/${item.compliance_id}`, payload);
        toast.success('Compliance item bijgewerkt');
      } else {
        await api.post('/compliance-items', payload);
        toast.success('Compliance item aangemaakt');
      }
      onSuccess();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fout bij opslaan');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="compliance-item-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">
            {isEdit ? 'Compliance item bewerken' : 'Nieuw compliance item'}
          </DialogTitle>
          <DialogDescription>
            {isEdit ? 'Pas de gegevens aan en sla op.' : 'Vul de gegevens in om een nieuw compliance item aan te maken.'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Row 1: Title */}
          <div>
            <Label htmlFor="compliance-title" className="text-xs font-medium">Titel *</Label>
            <Input
              id="compliance-title"
              data-testid="compliance-item-title-input"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="Bijv. Jaarlijkse keuring wasinstallatie"
              required
              className="mt-1"
            />
          </div>

          {/* Row 2: Type + Location */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs font-medium">Type *</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger className="mt-1" data-testid="compliance-item-type-select">
                  <SelectValue placeholder="Kies type" />
                </SelectTrigger>
                <SelectContent position="popper" sideOffset={4}>
                  {COMPLIANCE_TYPES.map(t => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-medium">Locatie *</Label>
              <Select value={form.location_id} onValueChange={(v) => setForm({ ...form, location_id: v })}>
                <SelectTrigger className="mt-1" data-testid="compliance-item-location-select">
                  <SelectValue placeholder="Kies locatie" />
                </SelectTrigger>
                <SelectContent position="popper" sideOffset={4} className="max-h-[200px]">
                  {locations.map(loc => (
                    <SelectItem key={loc.location_id} value={loc.location_id}>{loc.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Row 3: Due date + Recurrence */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="compliance-due-date" className="text-xs font-medium">Vervaldatum *</Label>
              <Input
                id="compliance-due-date"
                type="date"
                data-testid="compliance-item-due-date"
                value={form.due_date}
                onChange={(e) => setForm({ ...form, due_date: e.target.value })}
                required
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-xs font-medium">Herhaling</Label>
              <Select value={form.recurrence_interval} onValueChange={(v) => setForm({ ...form, recurrence_interval: v })}>
                <SelectTrigger className="mt-1" data-testid="compliance-item-recurrence-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent position="popper" sideOffset={4}>
                  {RECURRENCE_OPTIONS.map(r => (
                    <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Row 4: Assigned to + Reminders */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs font-medium">Toegewezen aan *</Label>
              <Select value={form.assigned_to} onValueChange={(v) => setForm({ ...form, assigned_to: v })}>
                <SelectTrigger className="mt-1" data-testid="compliance-item-assigned-to-select">
                  <SelectValue placeholder="Kies medewerker" />
                </SelectTrigger>
                <SelectContent position="popper" sideOffset={4}>
                  {users.map(u => (
                    <SelectItem key={u.email} value={u.email}>{u.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-medium">Herinneringen (dagen)</Label>
              <Select value={form.reminder_days} onValueChange={(v) => setForm({ ...form, reminder_days: v })}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent position="popper" sideOffset={4}>
                  {REMINDER_PRESETS.map(r => (
                    <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Row 5: Description */}
          <div>
            <Label htmlFor="compliance-desc" className="text-xs font-medium">Omschrijving</Label>
            <Textarea
              id="compliance-desc"
              data-testid="compliance-item-notes-textarea"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Optionele toelichting..."
              rows={3}
              className="mt-1"
            />
          </div>

          {/* Visibility */}
          <VisibilitySelector
            value={form.visibility}
            onChange={(v) => setForm({ ...form, visibility: v, ...(v === 'normal' ? { visibility_granted_users: [] } : {}) })}
            grantedUsers={form.visibility_granted_users}
            onGrantedUsersChange={(u) => setForm({ ...form, visibility_granted_users: u })}
            userRole={currentUser?.role}
            allUsers={users || []}
          />

          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              data-testid="compliance-item-cancel-button"
            >
              Annuleren
            </Button>
            <Button
              type="submit"
              disabled={saving}
              className="bg-[#2E75B6] hover:bg-[#27649C] text-white"
              data-testid="compliance-item-save-button"
            >
              {saving && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
              {isEdit ? 'Opslaan' : 'Aanmaken'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
